import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button, Card, Alert, Tab, Tabs, Table, Badge, Form, Row, Col, Spinner } from 'react-bootstrap';
import { getSiteDetails, generateCertificate, deleteSite, addDomainToSite, getEmailMapping, setEmailMapping, deleteEmailMapping } from '../services/api';
import EmailRoutingEditor from './EmailRoutingEditor';
import PublishingHistory from './PublishingHistory';

function SiteDetails({ tenancyEnabled = false }) {
  const { siteId } = useParams();
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [showDomainForm, setShowDomainForm] = useState(false);
  const [newDomain, setNewDomain] = useState({
    domain: '',
    add_www: true,
  });
  const [generatingCertificate, setGeneratingCertificate] = useState(false);
  const [addingDomain, setAddingDomain] = useState(false);
  const [emailMappings, setEmailMappings] = useState({});
  const [emailLoading, setEmailLoading] = useState({});
  const [emailServiceAvailable, setEmailServiceAvailable] = useState(true);
  const [editingDomain, setEditingDomain] = useState(null);
  // Saved {email, routes} per domain, kept apart from emailMappings, which the address editor changes as you type
  const [savedMappings, setSavedMappings] = useState({});
  const [routingDomain, setRoutingDomain] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSiteDetails();
  }, [siteId]);

  useEffect(() => {
    if (site) {
      fetchEmailMapping();
    } else {
      setEmailMappings({});
      setSavedMappings({});
      setEmailLoading({});
    }
  }, [site]);

  const fetchSiteDetails = async () => {
    try {
      setLoading(true);
      const data = await getSiteDetails(siteId);
      setSite(data);
      setError(null);
    } catch (err) {
      setError('Failed to load site details');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailMapping = async () => {
    if (!site) {
      setEmailServiceAvailable(true);
      setEmailMappings({});
      setSavedMappings({});
      setEmailLoading({});
      return;
    }

    const emailDomains = getAllDomainsForEmail(site);

    if (emailDomains.length === 0) {
      setEmailServiceAvailable(true);
      setEmailMappings({});
      setSavedMappings({});
      setEmailLoading({});
      return;
    }

    setEmailServiceAvailable(true);

    const initialLoading = emailDomains.reduce((acc, domain) => {
      acc[domain] = true;
      return acc;
    }, {});

    setEmailLoading(initialLoading);

    const mappings = {};
    const saved = {};
    const loadingState = { ...initialLoading };
    let serviceAvailable = true;

    for (const domain of emailDomains) {
      try {
        const mapping = await getEmailMapping(siteId, domain);
        mappings[domain] = mapping?.email || null;
        saved[domain] = { email: mapping?.email || null, routes: mapping?.routes || [] };
      } catch (err) {
        console.error(`Failed to fetch email for ${domain}:`, err);
        mappings[domain] = 'error'; // Special state for failed fetches
        if (err.response && err.response.status === 503) {
          serviceAvailable = false;
          setEmailServiceAvailable(false);
          break;
        }
      } finally {
        loadingState[domain] = false;
      }
    }

    if (!serviceAvailable) {
      emailDomains.forEach((domain) => {
        mappings[domain] = 'error';
        loadingState[domain] = false;
      });
    }

    setEmailLoading({ ...loadingState });
    setEmailMappings(mappings);
    setSavedMappings(saved);
  };

  const handleDeleteSite = async () => {
    if (window.confirm(`Are you sure you want to delete this site?`)) {
      try {
        await deleteSite(siteId);
        setMessage({ text: 'Site deleted successfully!', type: 'success' });
        navigate("/")
      } catch (err) {
        setMessage({ text: 'Failed to delete site', type: 'danger' });
      }
    }
  };

  const handleGenerateCertificate = async () => {
    if (!window.confirm("Are you sure you want to generate a new SSL certificate? This process may take a minute or two.")) {
      return;
    }

    try {
      setGeneratingCertificate(true);
      setMessage({
        text: "Generating certificate... Please wait. This process may take a minute.",
        type: "info"
      });
      
      const response = await generateCertificate(siteId);
      
      if (response.success) {
        setMessage({
          text: "Certificate generated successfully! Nginx has been reloaded.",
          type: "success"
        });
        // Refresh site details to show updated certificate information
        await fetchSiteDetails();
      } else {
        setMessage({
          text: `Failed to generate certificate: ${response.message}`,
          type: "danger"
        });
      }
    } catch (err) {
      setMessage({
        text: `Failed to generate certificate: ${err.message || "Unknown error"}`,
        type: "danger"
      });
    } finally {
      setGeneratingCertificate(false);
    }
  };

  const handleAddDomain = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!newDomain.domain || !newDomain.domain.includes('.')) {
      setMessage({ text: 'Please enter a valid domain name', type: 'danger' });
      return;
    }
    
    try {
      setAddingDomain(true);
      setMessage({ text: 'Adding domain...', type: 'info' });
      
      const response = await addDomainToSite(siteId, newDomain);
      
      if (response.success) {
        setMessage({ text: response.message, type: 'success' });
        setShowDomainForm(false);
        setNewDomain({ domain: '', add_www: true });
        await fetchSiteDetails();
      } else {
        setMessage({ text: response.message || 'Failed to add domain', type: 'danger' });
      }
    } catch (err) {
      setMessage({
        text: err.message || 'Failed to add domain',
        type: 'danger'
      });
    } finally {
      setAddingDomain(false);
    }
  };

  const handleSaveEmail = async (e) => {
    e.preventDefault();
    
    // Handle potential array or string from state
    let rawValue = emailMappings[editingDomain];
    if (Array.isArray(rawValue)) rawValue = rawValue.join('\n');
    
    if (!editingDomain || !rawValue) {
      setMessage({ text: 'Please enter an email address.', type: 'danger' });
      return;
    }

    // Parse input into a clean list
    const emails = rawValue.split(/[\n,]+/) // Split by newline or comma
      .map(e => e.trim())
      .filter(e => e.length > 0);

    if (emails.length === 0) {
      setMessage({ text: 'Please enter at least one valid email.', type: 'danger' });
      return;
    }

    // Basic validation
    const invalid = emails.find(e => !e.includes('@'));
    if (invalid) {
      setMessage({ text: `Invalid email format: ${invalid}`, type: 'danger' });
      return;
    }

    try {
      setMessage({text: 'Saving email...', type: 'info'});
      // Send the array directly; the backend handles lists now
      await setEmailMapping(siteId, editingDomain, emails);
      setMessage({ text: 'Email mapping saved successfully!', type: 'success' });
      fetchEmailMapping(); // Refresh state from server
    } catch (err) {
      if (err.message && err.message.includes('unavailable')) {
        setEmailServiceAvailable(false);
        setMessage({ text: 'Email service is currently unavailable. Please try again later.', type: 'danger' });
      } else {
        setMessage({ text: err.message || 'Failed to save email mapping.', type: 'danger' });
      }
    } finally {
      setEditingDomain(null);
    }
  };

  // Errors are rethrown so the routing editor can show them and stay open.
  // The API needs the default addresses with the rules, so they are re-read first:
  // the copy from page load may predate an address edit made since.
  const handleSaveRoutes = async (routes) => {
    try {
      const current = await getEmailMapping(siteId, routingDomain);
      if (!current?.email) {
        throw new Error('This domain has no default address any more. Close the editor and reload the page.');
      }
      await setEmailMapping(siteId, routingDomain, current.email, routes);
    } catch (err) {
      if (err.message && err.message.includes('unavailable')) {
        setEmailServiceAvailable(false);
      }
      throw err;
    }
    setRoutingDomain(null);
    setMessage({ text: 'Routing rules saved successfully!', type: 'success' });
    fetchEmailMapping(); // Refresh state from server
  };

  const handleDeleteEmail = async (domain) => {
    const ruleCount = savedMappings[domain]?.routes?.length || 0;
    const ruleWarning = ruleCount ? ` This also removes its ${ruleCount} routing rule${ruleCount === 1 ? '' : 's'}.` : '';
    if (window.confirm(`Are you sure you want to delete the email mapping for ${domain}?${ruleWarning}`)) {
      try {
        setMessage({text: `Deleting email for ${domain}...`, type: 'info'});
        await deleteEmailMapping(siteId, domain);
        setMessage({ text: 'Email mapping deleted successfully!', type: 'success' });
        fetchEmailMapping(); // Refresh state from server
      } catch (err) {
        if (err.message && err.message.includes('unavailable')) {
          setEmailServiceAvailable(false);
          setMessage({ text: 'Email service is currently unavailable. Please try again later.', type: 'danger' });
        } else {
          setMessage({ text: err.message || 'Failed to delete email mapping.', type: 'danger' });
        }
      }
    }
  };

  const handleRenameSite = async (e) => {
    alert("Functionality not added yet")
    // e.preventDefault();
    // try {
    //   // Assuming you have an API function for this
    //   await renameSite(siteId, { new_name: newName, update_site_path: updatePath });
    //   setMessage({ text: 'Site renamed successfully!', type: 'success' });
    //   setShowRenameForm(false);
    //   fetchSiteDetails();
    // } catch (err) {
    //   setMessage({ text: 'Failed to rename site', type: 'danger' });
    // }
  };

  const formatExpiryDate = (dateString) => {
    if (!dateString) return 'Unknown';
    
    const expiryDate = new Date(dateString);
    const now = new Date();
    const daysRemaining = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
    
    return (
      <div>
        {dateString}
        <div>
          {daysRemaining > 0 ? (
            <Badge bg="success" className="mt-1">
              {daysRemaining} days remaining
            </Badge>
          ) : (
            <Badge bg="danger" className="mt-1">
              Expired
            </Badge>
          )}
        </div>
      </div>
    );
  };

  // Function to determine if a site has any external domains
  const hasExternalDomains = (site) => {
    if (!site?.config?.domains || !site?.config?.site_path) return false;
    // An external domain is one that doesn't contain the site_path in its name (which is used for internal domains)
    // This is a more robust check than just checking for 'internal.getmogged.co.nz'
    return site.config.domains.some(domain => !domain.name.includes(site.config.site_path));
  };
  
  const getExternalDomains = (site) => {
    if (!site?.config?.domains || !site?.config?.site_path) return [];

    // Using the same logic as hasExternalDomains for consistency
    const externalDomainNames = site.config.domains
        .filter(d => !d.name.includes(site.config.site_path))
        .map(d => d.name);

    const baseDomains = new Set();
    externalDomainNames.forEach(name => {
        baseDomains.add(name.replace(/^www\./, ''));
    });

    return Array.from(baseDomains).sort();
  };

  const getAllDomainsForEmail = (site) => {
    if (!site?.config?.domains) return [];

    // All domains including internal — for email mapping
    const baseDomains = new Set();
    site.config.domains.forEach(d => {
        baseDomains.add(d.name.replace(/^www\./, ''));
    });

    return Array.from(baseDomains).sort();
  };

  if (loading && !site) return <div>Loading...</div>;
  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!site) return <Alert variant="warning">Site not found</Alert>;

  // Find the internal domain for display. Use site_path as a reliable indicator.
  const internalDomain = site.config.domains.find(d => d.name.includes(site.config.site_path));
  
  return (
    <div>
      <div className="d-flex justify-content-between mb-4">
        <div>
          <h1>{site.config.name}</h1>
        </div>
        <div>
          <Button 
            variant="danger" 
            className="me-2" 
            onClick={handleDeleteSite}
          >
            Delete
          </Button>
          <Button 
            as={Link} 
            to={`/sites/${siteId}/files/`} 
            variant="primary" 
            className="me-2"
          >
            Manage Files
          </Button>
          <Button as={Link} to={`/sites/${siteId}/staging`} variant="outline-primary" className="me-2">Staging</Button>
          {!hasExternalDomains(site) ? (
            <Button 
              variant="success" 
              onClick={() => setShowDomainForm(true)}
            >
              Add Domain
            </Button>
          ) : !site.status.has_valid_cert && (
            <Button 
              variant="success" 
              onClick={handleGenerateCertificate}
              disabled={generatingCertificate}
            >
              {generatingCertificate ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Generating Certificate...
                </>
              ) : (
                "Generate Certificate"
              )}
            </Button>
          )}
        </div>
      </div>

      {message && (
        <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* Domain Form */}
      {showDomainForm && (
        <Card className="mb-4">
          <Card.Header>Add External Domain</Card.Header>
          <Card.Body>
            <Form onSubmit={handleAddDomain}>
              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={3}>Domain Name</Form.Label>
                <Col sm={9}>
                  <Form.Control 
                    type="text" 
                    placeholder="example.com" 
                    value={newDomain.domain}
                    onChange={(e) => setNewDomain({ ...newDomain, domain: e.target.value })}
                    required
                  />
                  <Form.Text className="text-muted">
                    Enter the domain name without the 'www.' prefix.
                  </Form.Text>
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3">
                <Col sm={{ span: 9, offset: 3 }}>
                  <Form.Check
                    type="checkbox"
                    label="Add www variant (e.g., www.example.com)"
                    checked={newDomain.add_www}
                    onChange={(e) =>
                      setNewDomain({ ...newDomain, add_www: e.target.checked })
                    }
                  />
                </Col>
              </Form.Group>
              
              <div className="d-flex justify-content-end">
                <Button variant="secondary" onClick={() => setShowDomainForm(false)} className="me-2">
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={addingDomain}>
                  {addingDomain ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Adding Domain...
                    </>
                  ) : (
                    "Add Domain"
                  )}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      )}

      <Tabs defaultActiveKey="overview" className="mb-4">
        {tenancyEnabled && <Tab eventKey="publishing" title="Customer publishing">
          <PublishingHistory siteId={siteId} />
        </Tab>}
        <Tab eventKey="overview" title="Overview">
          <Card>
            <Card.Body>
              <h5>Site Information</h5>
              <p><strong>Name:</strong> {site.config.name}</p>
              <p><strong>Site Path:</strong> {site.config.site_path}</p>
              <p>
                <strong>Internal URL:</strong> {" "}
                {internalDomain && (
                  <a href={`https://${internalDomain.name}`} target="_blank" rel="noopener noreferrer">
                    {internalDomain.name}
                  </a>
                )}
              </p>
              
              <h5 className="mt-4">Domains</h5>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Domain</th>
                    <th>Certificate</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {site.config.domains.map((domain, index) => (
                    <tr key={index}>
                      <td>
                        <a href={`https://${domain.name}`} target="_blank" rel="noopener noreferrer">
                          {domain.name}
                        </a>
                      </td>
                      <td>
                        {domain.has_certificate ? (
                          <Badge bg="success">Yes</Badge>)
                         : domain.name.includes(site.config.site_path) ? (
                          <Badge bg="success">Wildcard</Badge>
                        ) : (
                          <Badge bg="warning">Missing</Badge>
                        )}
                      </td>
                      <td>
                        {!domain.name.includes(site.config.site_path) && (
                          <Button variant="danger" size="sm">
                            Remove
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              
              {!hasExternalDomains(site) && (
                <Alert variant="info">
                  This site doesn't have any external domains configured yet. 
                  <Button 
                    variant="success" 
                    size="sm" 
                    className="ms-3"
                    onClick={() => setShowDomainForm(true)}
                  >
                    Add Domain
                  </Button>
                </Alert>
              )}
              
              {hasExternalDomains(site) && (
                <div className="mt-3">
                  <Button 
                    variant="primary" 
                    size="sm" 
                    onClick={() => setShowDomainForm(true)}
                  >
                    Add Another Domain
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>
        
        <Tab eventKey="certificates" title="Certificates">
          <Card>
            <Card.Body>
              {site.certificates && site.certificates.length > 0 ? (
                <>
                  <h5>SSL/TLS Certificates</h5>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Domain</th>
                        <th>Status</th>
                        <th>Expiry Date</th>
                        <th>Issuer</th>
                        <th>Covered Domains</th>
                      </tr>
                    </thead>
                    <tbody>
                      {site.certificates.map((cert, index) => (
                        <tr key={index}>
                          <td>{cert.domain}</td>
                          <td>
                            {cert.is_valid ? 
                              <Badge bg="success">Valid</Badge> : 
                              <Badge bg="danger">Invalid</Badge>
                            }
                          </td>
                          <td>{formatExpiryDate(cert.expiry_date)}</td>
                          <td>{cert.issuer || 'Unknown'}</td>
                          <td>
                            {cert.domains && cert.domains.length > 0 ? (
                              <ul className="mb-0 ps-3">
                                {cert.domains.map((domain, idx) => (
                                  <li key={idx}>{domain}</li>
                                ))}
                              </ul>
                            ) : 'No domains specified'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  
                  {/* Add certificate generation button in the certificates tab as well */}
                  {hasExternalDomains(site) && (
                    <Button 
                      variant="success" 
                      onClick={handleGenerateCertificate}
                      disabled={generatingCertificate}
                      className="mt-3"
                    >
                      {generatingCertificate ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Generating Certificate...
                        </>
                      ) : (
                        "Regenerate Certificates"
                      )}
                    </Button>
                  )}
                </>
              ) : (
                <Alert variant="info">
                  No certificates have been generated for this site.
                  {hasExternalDomains(site) && !site.status.has_valid_cert && (
                    <div className="mt-3">
                      <Button 
                        variant="success" 
                        onClick={handleGenerateCertificate}
                        disabled={generatingCertificate}
                      >
                        {generatingCertificate ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            Generating Certificate...
                          </>
                        ) : (
                          "Generate Certificate"
                        )}
                      </Button>
                    </div>
                  )}
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="email" title="Email">
          <Card>
            <Card.Body>
              <h5>Email Forwarding</h5>
              {!emailServiceAvailable ? (
                <Alert variant="warning">
                  <h5>Email Service Unavailable</h5>
                  <p>The email forwarding service is currently unavailable. Please try again later.</p>
                </Alert>
              ) : getAllDomainsForEmail(site).length === 0 ? (
                <Alert variant="info">No domains configured yet.</Alert>
              ) : (
                <Table striped bordered>
                  <thead>
                    <tr>
                      <th>Domain</th>
                      <th>Destinations</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getAllDomainsForEmail(site).map(domain => (
                      <tr key={domain}>
                        <td><strong>{domain}</strong></td>
                        <td>
                          {emailLoading[domain] ? (
                            <Spinner animation="border" size="sm" />
                          ) : editingDomain === domain ? (
                            <Form onSubmit={handleSaveEmail}>
                              <Form.Control 
                                as="textarea" 
                                rows={Math.max(2, Array.isArray(emailMappings[domain]) ? emailMappings[domain].length : 1)}
                                placeholder="Enter emails (one per line)"
                                value={
                                  Array.isArray(emailMappings[domain]) 
                                    ? emailMappings[domain].join('\n') 
                                    : (emailMappings[domain] || '')
                                }
                                onChange={(e) => setEmailMappings(prev => ({ ...prev, [domain]: e.target.value }))}
                                autoFocus
                              />
                            </Form>
                          ) : emailMappings[domain] === 'error' ? (
                            <Badge bg="danger">Error loading</Badge>
                          ) : emailMappings[domain] ? (
                            Array.isArray(emailMappings[domain]) ? (
                              <div className="d-flex flex-column gap-1 align-items-start">
                                {emailMappings[domain].map((email, idx) => (
                                  <Badge key={idx} bg="info" className="fw-normal text-break">
                                    {email}
                                  </Badge>
                                ))}
                              </div>
                            ) : (
                              <span>{emailMappings[domain]}</span>
                            )
                          ) : (
                            <span className="text-muted">Not set</span>
                          )}
                          {!emailLoading[domain] && editingDomain !== domain && savedMappings[domain]?.routes?.length > 0 && (
                            <div className="mt-2 small">
                              <div className="text-muted">Routing rules (first match wins; other leads go to the addresses above):</div>
                              {savedMappings[domain].routes.map((rule, idx) => (
                                <div key={idx} className="text-break">
                                  {rule.field} = {rule.value} → {[].concat(rule.email).join(', ')}
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td>
                          {editingDomain === domain ? (
                             <>
                              <Button variant="success" size="sm" onClick={handleSaveEmail} className="me-2">Save</Button>
                              <Button variant="secondary" size="sm" onClick={() => setEditingDomain(null)}>Cancel</Button>
                             </>
                          ) : (
                            <>
                              <Button variant="outline-primary" size="sm" onClick={() => setEditingDomain(domain)} className="me-2">
                                {emailMappings[domain] ? 'Edit' : 'Add'}
                              </Button>
                              {savedMappings[domain]?.email && emailMappings[domain] !== 'error' && (
                                <Button variant="outline-secondary" size="sm" onClick={() => setRoutingDomain(domain)} className="me-2">
                                  Routing
                                </Button>
                              )}
                              {emailMappings[domain] && emailMappings[domain] !== 'error' && (
                                <Button variant="outline-danger" size="sm" onClick={() => handleDeleteEmail(domain)}>
                                  Delete
                                </Button>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
              {routingDomain && (
                <EmailRoutingEditor
                  domain={routingDomain}
                  routes={savedMappings[routingDomain]?.routes || []}
                  onHide={() => setRoutingDomain(null)}
                  onSave={handleSaveRoutes}
                />
              )}
            </Card.Body>
          </Card>
        </Tab>
        
        <Tab eventKey="config" title="Nginx Configuration">
          <Card>
            <Card.Body>
              {site.nginx_config ? (
                <pre className="bg-light p-3">{site.nginx_config}</pre>
              ) : (
                <Alert variant="warning">No Nginx configuration found</Alert>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    </div>
  );
}

export default SiteDetails;
